import React, { useState, useEffect } from "react"
import {
  Row,
  Col,
  Button,
  Input,
  FormGroup,
  Label,
  Alert,
  Spinner,
} from "reactstrap"
import { Formik, Form, Field } from "formik"
import * as Yup from "yup"
import Flatpickr from "react-flatpickr"
import "flatpickr/dist/themes/material_blue.css"
import { Spanish } from "flatpickr/dist/l10n/es"

import {
  saveSiembra,
  updateSiembra,
  calcularValorCosecha,
  getCultivos,
  getCampanas,
  getEstadosFenologicos,
  getSugerenciaSiembra,
  getValoresGenerales,
} from "api/agro"
import toastHandler from "toastHandler"
import { formatearNumero } from "helpers/formatters"
import AmountInput from "components/Input/AmountInput"
import useAuthorization from "hooks/useAuthorization"

const getSchema = establecimientos =>
  Yup.object().shape({
    idEstablecimiento: Yup.number()
      .typeError("Seleccione establecimiento")
      .required("Seleccione establecimiento"),
    codigoCultivo: Yup.string().required("Seleccione cultivo"),
    codigoCampana: Yup.string().required("Seleccione campaña"),
    superficieHa: Yup.number()
      .typeError("Debe ser un número")
      .positive("La superficie debe ser mayor a 0")
      .required("Requerido")
      .test(
        "max-establecimiento",
        "No puede superar la superficie del establecimiento",
        function (value) {
          if (!value) return true
          const { idEstablecimiento } = this.parent
          if (!idEstablecimiento) return true
          const est = establecimientos.find(
            e => e.id === Number(idEstablecimiento),
          )
          if (!est || !est.superficieTotalHa) return true
          return value <= Number(est.superficieTotalHa)
        },
      ),
    fechaSiembra: Yup.date().required("Seleccione fecha de siembra"),
    rendimientoEsperadoQqHa: Yup.number()
      .typeError("Debe ser un número")
      .positive("El rendimiento debe ser mayor a 0")
      .max(999999.9, "El máximo es 999,999.90")
      .required("Requerido"),
    maquinariaPropia: Yup.boolean()
      .nullable()
      .transform(value => (value === "" ? null : value)),
  })

const FormSiembra = ({
  establecimientos,
  onSuccess,
  onCancel,
  siembraAEditar,
}) => {
  // BDSJEA-208 hotfix: antes leia userStore(state => state.modificacion), que
  // es un flag global seteado por RequireRole segun el objeto de la RUTA
  // (/agro/busqueda/productor). Ese objeto es distinto del que ya gatea
  // abrir este formulario y ver Editar/Eliminar en ActividadAgricola.js
  // (/agro/establecimientos), asi que un perfil con permiso en uno y no en
  // el otro (caso analista) podia abrir "Nueva Siembra" sin ver el boton
  // Guardar. Se usa el mismo objeto que ActividadAgricola.js para que sea
  // consistente con lo que ya decide si el modal se puede abrir.
  const { puedeModificarObjeto } = useAuthorization()
  const modificacion = puedeModificarObjeto("/agro/establecimientos")
  const [cultivos, setCultivos] = useState([])
  const [campanas, setCampanas] = useState([])
  const [estadosFenologicos, setEstadosFenologicos] = useState([])
  const [valorEstimado, setValorEstimado] = useState(null)
  const [calculando, setCalculando] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  // BDSJEA-165: sugerencia de rendimiento y costo segun cultivo y zona del
  // establecimiento. Se guarda entera y no solo el numero porque el detalle del
  // origen es lo que le permite al analista decidir si lo pisa.
  const [sugerencia, setSugerencia] = useState(null)
  const [buscandoSugerencia, setBuscandoSugerencia] = useState(false)
  // Cotizacion BCRA (valor_general TIPO_CAMBIO_USD) para convertir a USD
  // los precios que vienen solo en ARS.
  const [tipoCambioBcra, setTipoCambioBcra] = useState(null)

  const setSuccess = toastHandler(s => s.setSuccess)
  const setError = toastHandler(s => s.setError)

  useEffect(() => {
    loadDominios()
    getValoresGenerales()
      .then(data => {
        const tc = (data || []).find(v => v.codigo === "TIPO_CAMBIO_USD")
        if (tc?.valor) setTipoCambioBcra(parseFloat(tc.valor))
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (siembraAEditar?.codigoCultivo) {
      getEstadosFenologicos(siembraAEditar.codigoCultivo)
        .then(setEstadosFenologicos)
        .catch(() => setEstadosFenologicos([]))
    }
  }, [siembraAEditar])

  const loadDominios = async () => {
    try {
      const [cultivosData, campanasData] = await Promise.all([
        getCultivos(),
        getCampanas(),
      ])
      setCultivos(cultivosData)
      setCampanas(campanasData)
    } catch (error) {
      console.error("Error cargando dominios:", error)
    }
  }

  /**
   * BDSJEA-165 - Pide el rendimiento y el costo que la proyeccion usaria si el
   * productor no declarara nada.
   *
   * El campo se pre-llena solo cuando el backend lo habilita (sugerencia
   * .precargar): hace falta que haya numero, que salga de datos reales y que la
   * zona se haya resuelto por el departamento del establecimiento. Si la zona
   * salio del default provincial el numero viaja igual, pero como referencia
   * que hay que aceptar con un click. Un valor que aparece escrito en el campo
   * se guarda aunque nadie lo haya mirado.
   *
   * El valor pre-cargado entra marcado como SUGERIDO. Pasa a DECLARADO si el
   * usuario lo edita o si acepta la referencia: de eso depende que la proyeccion
   * vuelva a correr su cadena en lugar de tomar este numero como palabra del
   * productor, y que la alerta de rendimiento declarado no se dispare contra un
   * valor que puso el sistema solo.
   */
  const cargarSugerencia = async (
    idEstablecimiento,
    codigoCultivo,
    values,
    setFieldValue,
  ) => {
    if (!idEstablecimiento || !codigoCultivo) {
      setSugerencia(null)
      return
    }
    setBuscandoSugerencia(true)
    try {
      const data = await getSugerenciaSiembra(idEstablecimiento, codigoCultivo)
      setSugerencia(data)

      // No se pisa lo que el usuario ya escribio.
      const yaCargado =
        values.rendimientoEsperadoQqHa !== "" &&
        values.rendimientoEsperadoQqHa !== null &&
        values.rendimientoOrigen === "DECLARADO"

      const precargar = Boolean(data && data.precargar && !yaCargado)

      // Si no corresponde pre-cargar y en el campo habia una sugerencia previa,
      // se limpia: seria el numero del cultivo anterior.
      const limpiarSugerenciaPrevia =
        !precargar && !yaCargado && values.rendimientoOrigen === "SUGERIDO"

      let rendimientoAplicado = values.rendimientoEsperadoQqHa
      if (precargar) {
        rendimientoAplicado = Number(data.rendimientoQqHa)
        setFieldValue("rendimientoEsperadoQqHa", Number(data.rendimientoQqHa))
        setFieldValue("rendimientoOrigen", "SUGERIDO")
      } else if (limpiarSugerenciaPrevia) {
        rendimientoAplicado = ""
        setFieldValue("rendimientoEsperadoQqHa", "")
      }

      // El Valor Estimado cuelga del onBlur de superficie y de rendimiento, y
      // setFieldValue no dispara blur: al pre-llenar quedaba en "-" aunque ya
      // hubiera con que calcularlo. Se recalcula a mano.
      //
      // Tambien hace falta cuando el rendimiento no se toca pero el cultivo si:
      // el valor estimado depende del precio del cultivo, asi que el numero que
      // hubiera quedado en pantalla seria del cultivo anterior.
      handleCalcularValor(
        codigoCultivo,
        values.superficieHa,
        rendimientoAplicado,
      )
    } catch (error) {
      // Que no haya sugerencia no debe impedir cargar la siembra a mano.
      console.warn("No se pudo obtener la sugerencia de rendimiento:", error)
      setSugerencia(null)
    } finally {
      setBuscandoSugerencia(false)
    }
  }

  const handleCultivoChange = async (codigoCultivo, setFieldValue, values) => {
    setFieldValue("codigoCultivo", codigoCultivo)
    setFieldValue("codigoEstadoFenologico", "")
    cargarSugerencia(
      values.idEstablecimiento,
      codigoCultivo,
      values,
      setFieldValue,
    )
    if (codigoCultivo) {
      try {
        const estados = await getEstadosFenologicos(codigoCultivo)
        setEstadosFenologicos(estados)
      } catch {
        setEstadosFenologicos([])
      }
    } else {
      setEstadosFenologicos([])
    }
  }

  /**
   * BDSJEA-165 - Como se describe el origen del rendimiento en una linea.
   *
   * Existe para el bloque de Calculo: ahi conviven un rendimiento que puede
   * venir de la parametria del banco y un precio que viene del mercado, y sin
   * etiquetar cual es cual el analista lee una inconsistencia donde no la hay.
   */
  const textoOrigenRendimiento = (values, sug) => {
    if (values.rendimientoOrigen === "DECLARADO") return "cargado a mano"
    if (!sug) return null
    switch (sug.origenRendimiento) {
      case "MAGYP":
        return "serie oficial MAGyP"
      case "VALOR_AGRO":
        return sug.nombreZona
          ? `parametría del banco, zona ${sug.nombreZona}`
          : "parametría del banco"
      case "CONFIGURACION":
        return "parámetro general del cultivo"
      case "DEFAULT":
        return "valor genérico"
      default:
        return null
    }
  }

  const handleCalcularValor = async (
    codigoCultivo,
    superficie,
    rendimiento,
  ) => {
    if (!codigoCultivo || !superficie || !rendimiento) {
      setValorEstimado(null)
      return
    }
    setCalculando(true)
    try {
      const resultado = await calcularValorCosecha(
        codigoCultivo,
        superficie,
        rendimiento,
      )
      setValorEstimado(resultado.error ? { error: resultado.error } : resultado)
    } catch (error) {
      setValorEstimado({ error: error.message })
    } finally {
      setCalculando(false)
    }
  }

  const handleSubmit = async (values, { setSubmitting, validateForm }) => {
    const formErrors = await validateForm()
    if (Object.keys(formErrors).length > 0) {
      setSubmitted(true)
      toastHandler.getState().setError("Complete los campos obligatorios")
      setSubmitting(false)
      return
    }
    try {
      if (siembraAEditar) {
        await updateSiembra(siembraAEditar.id, {
          superficieHa: parseFloat(values.superficieHa),
          rendimientoEsperadoQqHa: values.rendimientoEsperadoQqHa
            ? parseFloat(values.rendimientoEsperadoQqHa)
            : null,
          rendimientoOrigen: values.rendimientoOrigen || "DECLARADO",
          maquinariaPropia:
            values.maquinariaPropia === ""
              ? null
              : values.maquinariaPropia === "true" ||
                values.maquinariaPropia === true,
          codigoEstadoFenologico: values.codigoEstadoFenologico || null,
        })
        setSuccess("Siembra actualizada correctamente")
      } else {
        await saveSiembra({
          idEstablecimiento: parseInt(values.idEstablecimiento),
          codigoCultivo: values.codigoCultivo,
          codigoCampana: values.codigoCampana || null,
          variedad: values.variedad || null,
          superficieHa: parseFloat(values.superficieHa),
          fechaSiembra: values.fechaSiembra,
          codigoEstadoFenologico: values.codigoEstadoFenologico || null,
          rendimientoEsperadoQqHa: values.rendimientoEsperadoQqHa
            ? parseFloat(values.rendimientoEsperadoQqHa)
            : null,
          rendimientoOrigen: values.rendimientoOrigen || "DECLARADO",
          maquinariaPropia:
            values.maquinariaPropia === ""
              ? null
              : values.maquinariaPropia === "true" ||
                values.maquinariaPropia === true,
        })
        setSuccess("Siembra registrada correctamente")
      }
      onSuccess && onSuccess()
    } catch (error) {
      const msg = error.response?.data?.mensaje || error.message
      setError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  const campanaActual = campanas.find(c => c.activa)

  const initialValues = siembraAEditar
    ? {
        idEstablecimiento: siembraAEditar.idEstablecimiento || "",
        codigoCultivo: siembraAEditar.codigoCultivo || "",
        codigoCampana: siembraAEditar.campana || "",
        variedad: siembraAEditar.variedad || "",
        superficieHa: siembraAEditar.superficieHa || "",
        fechaSiembra: siembraAEditar.fechaSiembra
          ? new Date(siembraAEditar.fechaSiembra)
          : new Date(),
        codigoEstadoFenologico: siembraAEditar.codigoEstadoFenologico || "",
        rendimientoEsperadoQqHa: siembraAEditar.rendimientoEsperadoQqHa || "",
        rendimientoOrigen: siembraAEditar.rendimientoOrigen || "DECLARADO",
        maquinariaPropia:
          siembraAEditar.maquinariaPropia != null
            ? siembraAEditar.maquinariaPropia.toString()
            : "",
      }
    : {
        idEstablecimiento: "",
        codigoCultivo: "",
        codigoCampana: campanaActual?.codigo || "",
        variedad: "",
        superficieHa: "",
        fechaSiembra: new Date(),
        codigoEstadoFenologico: "",
        rendimientoEsperadoQqHa: "",
        rendimientoOrigen: "DECLARADO",
        maquinariaPropia: "",
      }

  return (
    <Formik
      key={
        siembraAEditar
          ? `edit-${siembraAEditar.id}`
          : campanaActual?.codigo || "sin-campana"
      }
      initialValues={initialValues}
      validationSchema={getSchema(establecimientos)}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        isSubmitting,
        setFieldValue,
        validateField,
      }) => {
        const showFieldError = field =>
          !!((touched[field] || submitted) && errors[field])

        // Preferir la cotizacion BCRA; si no esta disponible, el TC implicito
        // que devuelve el backend junto al par de precios ARS/USD.
        const tc = tipoCambioBcra || valorEstimado?.tipoCambioUsd
        const precioUsd =
          valorEstimado?.precioUsdTn ??
          (valorEstimado?.precioArsTn && tc
            ? valorEstimado.precioArsTn / tc
            : null)
        const valorUsd =
          valorEstimado?.valorBrutoUsd ??
          (valorEstimado?.valorBrutoArs && tc
            ? valorEstimado.valorBrutoArs / tc
            : null)

        return (
          <Form>
            <Row>
              {/* Establecimiento */}
              <Col md={5}>
                <FormGroup>
                  <Label>Establecimiento *</Label>
                  <Field
                    name="idEstablecimiento"
                    as={Input}
                    type="select"
                    invalid={showFieldError("idEstablecimiento")}
                    onChange={e => {
                      const id = e.target.value
                      setFieldValue("idEstablecimiento", id)
                      // BDSJEA-165: la zona sale del establecimiento, asi que
                      // cambiarlo cambia la sugerencia.
                      cargarSugerencia(
                        id,
                        values.codigoCultivo,
                        values,
                        setFieldValue,
                      )
                      const est = establecimientos.find(
                        est => est.id === Number(id),
                      )
                      if (est?.superficieTotalHa) {
                        setFieldValue(
                          "superficieHa",
                          Number(est.superficieTotalHa),
                        )
                      }
                    }}
                  >
                    <option value="">Seleccione...</option>
                    {establecimientos.map(est => (
                      <option key={est.id} value={est.id}>
                        {est.nombre}
                      </option>
                    ))}
                  </Field>
                  {showFieldError("idEstablecimiento") && (
                    <div className="text-danger small">
                      {errors.idEstablecimiento}
                    </div>
                  )}
                </FormGroup>
              </Col>

              {/* Campaña */}
              <Col md={4}>
                <FormGroup>
                  <Label>Campaña *</Label>
                  <Field
                    name="codigoCampana"
                    as={Input}
                    type="select"
                    invalid={showFieldError("codigoCampana")}
                  >
                    <option value="">Seleccione...</option>
                    {campanas.map(c => (
                      <option key={c.codigo} value={c.codigo}>
                        {c.codigo} {c.activa && "(Actual)"}
                      </option>
                    ))}
                  </Field>
                  {showFieldError("codigoCampana") && (
                    <div className="text-danger small">
                      {errors.codigoCampana}
                    </div>
                  )}
                </FormGroup>
              </Col>

              {/* Fecha Siembra */}
              <Col md={3}>
                <FormGroup>
                  <Label>Fecha Siembra *</Label>
                  <Flatpickr
                    className={
                      "form-control" +
                      (showFieldError("fechaSiembra") ? " is-invalid" : "")
                    }
                    value={values.fechaSiembra}
                    onChange={([date]) => setFieldValue("fechaSiembra", date)}
                    options={{ dateFormat: "d/m/Y", locale: Spanish }}
                  />
                  {showFieldError("fechaSiembra") && (
                    <div className="text-danger small">
                      {errors.fechaSiembra}
                    </div>
                  )}
                </FormGroup>
              </Col>

              {/* Cultivo */}
              <Col md={4}>
                <FormGroup>
                  <Label>Cultivo *</Label>
                  <Input
                    type="select"
                    value={values.codigoCultivo}
                    invalid={showFieldError("codigoCultivo")}
                    onChange={e =>
                      handleCultivoChange(e.target.value, setFieldValue, values)
                    }
                  >
                    <option value="">Seleccione...</option>
                    {cultivos.map(c => (
                      <option key={c.codigo} value={c.codigo}>
                        {c.nombre}
                      </option>
                    ))}
                  </Input>
                  {showFieldError("codigoCultivo") && (
                    <div className="text-danger small">
                      {errors.codigoCultivo}
                    </div>
                  )}
                </FormGroup>
              </Col>

              {/* Variedad */}
              <Col md={4}>
                <FormGroup>
                  <Label>Variedad</Label>
                  <Field
                    name="variedad"
                    as={Input}
                    placeholder="Ej: DM 40R16"
                  />
                </FormGroup>
              </Col>

              {/* Estado Fenológico */}
              <Col md={4}>
                <FormGroup>
                  <Label>Estado Fenológico</Label>
                  <Field name="codigoEstadoFenologico" as={Input} type="select">
                    <option value="">Seleccione...</option>
                    {estadosFenologicos.map(e => (
                      <option key={e.codigo} value={e.codigo}>
                        {e.descripcion}
                      </option>
                    ))}
                  </Field>
                </FormGroup>
              </Col>

              {/* Superficie */}
              <Col md={4}>
                <FormGroup>
                  <Label>Superficie (ha) *</Label>
                  <Field
                    name="superficieHa"
                    as={Input}
                    type="text"
                    inputMode="numeric"
                    invalid={showFieldError("superficieHa")}
                    value={formatearNumero(values.superficieHa, 0)}
                    onChange={e => {
                      const raw = e.target.value
                        .replace(/\./g, "")
                        .replace(",", ".")
                      const num = raw === "" ? "" : Number(raw)
                      setFieldValue("superficieHa", num)
                      validateField("superficieHa")
                    }}
                    onBlur={() => {
                      validateField("superficieHa")
                      handleCalcularValor(
                        values.codigoCultivo,
                        values.superficieHa,
                        values.rendimientoEsperadoQqHa,
                      )
                    }}
                  />
                  {showFieldError("superficieHa") && (
                    <div className="text-danger small">
                      {errors.superficieHa}
                    </div>
                  )}
                </FormGroup>
              </Col>

              {/* Rendimiento Esperado */}
              <Col md={4}>
                <FormGroup>
                  <Label>Rendimiento Esperado (qq/ha) *</Label>
                  <Field
                    name="rendimientoEsperadoQqHa"
                    as={Input}
                    type="text"
                    inputMode="numeric"
                    invalid={showFieldError("rendimientoEsperadoQqHa")}
                    value={formatearNumero(values.rendimientoEsperadoQqHa, 0)}
                    onChange={e => {
                      const raw = e.target.value
                        .replace(/\./g, "")
                        .replace(",", ".")
                      const num = raw === "" ? "" : Number(raw)
                      setFieldValue("rendimientoEsperadoQqHa", num)
                      // BDSJEA-165: lo toco una persona, deja de ser sugerido.
                      setFieldValue("rendimientoOrigen", "DECLARADO")
                      validateField("rendimientoEsperadoQqHa")
                    }}
                    onBlur={() => {
                      validateField("rendimientoEsperadoQqHa")
                      handleCalcularValor(
                        values.codigoCultivo,
                        values.superficieHa,
                        values.rendimientoEsperadoQqHa,
                      )
                    }}
                  />
                  {showFieldError("rendimientoEsperadoQqHa") && (
                    <div className="text-danger small">
                      {errors.rendimientoEsperadoQqHa}
                    </div>
                  )}

                  {/* BDSJEA-165: de donde salio el numero y si el sistema puede
                      sostenerlo. Sin esto el analista acepta una cifra a ciegas;
                      con esto puede evaluarla. */}
                  {buscandoSugerencia && (
                    <small className="text-muted d-block">
                      <i className="mdi mdi-loading mdi-spin me-1" />
                      Buscando rendimiento de referencia...
                    </small>
                  )}

                  {/* Sugerencia aplicada: la zona es la del departamento. */}
                  {!buscandoSugerencia &&
                    sugerencia &&
                    values.rendimientoOrigen === "SUGERIDO" &&
                    sugerencia.detalleRendimiento && (
                      <small className="text-muted d-block">
                        <i className="mdi mdi-information-outline me-1" />
                        {sugerencia.detalleRendimiento}
                        {sugerencia.nombreZona &&
                          ` Zona: ${sugerencia.nombreZona}.`}
                      </small>
                    )}

                  {/* Sin datos de rendimiento para esta zona: informar sin ofrecer
                      un número de otra zona como referencia (BDSJEA-205). */}
                  {!buscandoSugerencia &&
                    sugerencia &&
                    sugerencia.rendimientoQqHa == null &&
                    values.rendimientoOrigen !== "DECLARADO" && (
                      <small className="text-muted d-block">
                        <i className="mdi mdi-information-outline me-1" />
                        {sugerencia.detalleRendimiento ||
                          "No se encontraron datos de rendimiento para este cultivo en esta zona."}
                      </small>
                    )}

                  {/* Zona no confirmada con valor de referencia: no se pre-carga
                      nada, pero se ofrece el número con un click. */}
                  {!buscandoSugerencia &&
                    sugerencia &&
                    sugerencia.rendimientoQqHa != null &&
                    values.rendimientoOrigen !== "DECLARADO" &&
                    !sugerencia.precargar && (
                      <small className="text-muted d-block">
                        <i className="mdi mdi-alert-outline me-1" />
                        {sugerencia.motivoZona ||
                          sugerencia.detalleRendimiento}{" "}
                        {sugerencia.motivoZona
                          ? `Como referencia, la parametría de ${
                              sugerencia.nombreZona || "la provincia"
                            } da ${
                              sugerencia.rendimientoQqHa
                            } qq/ha, que puede no corresponder a este campo.`
                          : `Valor de referencia: ${sugerencia.rendimientoQqHa} qq/ha.`}{" "}
                        <Button
                          color="link"
                          size="sm"
                          className="p-0 align-baseline"
                          onClick={() => {
                            const v = Number(sugerencia.rendimientoQqHa)
                            setFieldValue("rendimientoEsperadoQqHa", v)
                            // Lo acepto una persona, asi que deja de ser
                            // sugerido. Es la misma semantica que editarlo a
                            // mano: DECLARADO significa que alguien se hizo
                            // cargo del numero, no que lo haya tecleado.
                            setFieldValue("rendimientoOrigen", "DECLARADO")
                            handleCalcularValor(
                              values.codigoCultivo,
                              values.superficieHa,
                              v,
                            )
                          }}
                        >
                          Usar {sugerencia.rendimientoQqHa} qq/ha
                        </Button>
                      </small>
                    )}

                  {!buscandoSugerencia &&
                    values.rendimientoOrigen === "DECLARADO" &&
                    sugerencia &&
                    sugerencia.rendimientoQqHa != null && (
                      <small className="text-muted d-block">
                        <i className="mdi mdi-pencil-outline me-1" />
                        Valor cargado a mano. Referencia{" "}
                        {sugerencia.zonaConfiable
                          ? `de ${sugerencia.nombreZona}`
                          : "provincial, con la zona sin confirmar"}
                        : {sugerencia.rendimientoQqHa} qq/ha.
                      </small>
                    )}
                </FormGroup>
              </Col>

              {/* Maquinaria Propia */}
              <Col md={4}>
                <FormGroup>
                  <Label>Maquinaria Propia</Label>
                  <Field name="maquinariaPropia" as={Input} type="select">
                    <option value="">Sin dato</option>
                    <option value="false">No</option>
                    <option value="true">Sí</option>
                  </Field>
                </FormGroup>
              </Col>

              {/* Precio de referencia (USD/tn) */}
              <Col md={4}>
                <FormGroup>
                  <Label>Precio (USD/tn)</Label>
                  {calculando ? (
                    <Spinner size="sm" />
                  ) : (
                    <AmountInput
                      value={precioUsd ?? ""}
                      onChange={() => {}}
                      currency=""
                      minDecimals={0}
                      nroDecimales={2}
                      disabled
                      placeholder="-"
                      className={
                        precioUsd != null ? "fw-bold text-primary" : ""
                      }
                    />
                  )}
                </FormGroup>
              </Col>

              {/* Valor Estimado (USD) */}
              <Col md={4}>
                <FormGroup>
                  <Label>Valor Estimado (USD)</Label>
                  {calculando ? (
                    <Spinner size="sm" />
                  ) : valorEstimado?.error ? (
                    <span className="text-muted small">
                      {valorEstimado.error}
                    </span>
                  ) : (
                    <AmountInput
                      value={valorUsd ?? ""}
                      onChange={() => {}}
                      currency=""
                      minDecimals={0}
                      nroDecimales={2}
                      disabled
                      placeholder="-"
                      className={valorUsd != null ? "fw-bold text-success" : ""}
                    />
                  )}
                </FormGroup>
              </Col>

              {/* Detalle del cálculo */}
              {valorEstimado && !valorEstimado.error && (
                <Col md={12}>
                  <Alert color="info" className="mt-2 py-2" fade={false}>
                    <small>
                      <strong>Cálculo:</strong>{" "}
                      {valorEstimado.produccionTotalTn?.toFixed(1)} tn ×{" "}
                      {precioUsd != null ? (
                        <>
                          USD{" "}
                          {precioUsd.toLocaleString("es-AR", {
                            maximumFractionDigits: 2,
                          })}
                          /tn
                          {" = "}
                          <strong>
                            USD{" "}
                            {Math.round(valorUsd ?? 0).toLocaleString("es-AR")}
                          </strong>
                        </>
                      ) : null}
                      {/* BDSJEA-165: los dos numeros del calculo vienen de
                          fuentes distintas y hay que decirlo. El rendimiento
                          sale de la cadena de proyeccion (MAGyP o parametria del
                          banco por zona); el precio, del mercado. Sin etiquetas,
                          un aviso de "no se pudo determinar la zona" arriba y un
                          precio firme abajo se leen como una contradiccion. */}
                      <span className="d-block mt-1 text-muted">
                        {textoOrigenRendimiento(values, sugerencia) && (
                          <>
                            Rendimiento:{" "}
                            {textoOrigenRendimiento(values, sugerencia)}
                            {" · "}
                          </>
                        )}
                        Precio: {valorEstimado.fuentePrecio} al{" "}
                        {new Date(valorEstimado.fechaPrecio).toLocaleDateString(
                          "es-AR",
                        )}
                        {valorEstimado.tipoCambioUsd && (
                          <>
                            {" · "}TC: $
                            {valorEstimado.tipoCambioUsd.toLocaleString(
                              "es-AR",
                              { maximumFractionDigits: 0 },
                            )}
                            /USD
                          </>
                        )}
                      </span>
                    </small>
                  </Alert>
                </Col>
              )}
            </Row>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button color="secondary" onClick={onCancel}>
                Cancelar
              </Button>
              {modificacion && (
                <Button color="primary" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <Spinner size="sm" />
                  ) : siembraAEditar ? (
                    "Guardar Cambios"
                  ) : (
                    "Guardar Siembra"
                  )}
                </Button>
              )}
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}

export { FormSiembra }
